import React, { FC } from 'react';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { useField as useFormikField } from 'formik';
import {
  DatePickerInput,
  DatePickerInputProps,
  TextFieldTheme,
} from 'wix-ui-tpa/cssVars';

import { Field, FieldWrapper } from '../../../../../../types';
import {
  useFieldLabel,
  useFieldPrivacyAriaLabel,
} from '../../../../../../hooks';
import { FieldLabel } from '../../FieldLabel';
import { FieldPrivacyIcon } from '../../FieldPrivacyIcon';
import { classes, st } from './DatePicker.st.css';

interface DatePickerProps extends FieldWrapper {
  allowFutureDates?: boolean;
}

const getDatePattern = (language: string): string => {
  return Intl
    ? Intl.DateTimeFormat(language, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      })
        .format(new Date(Date.UTC(2000, 11, 31, 0, 0, 0)))
        .replace('31', 'DD')
        .replace('12', 'MM')
        .replace('2000', 'YYYY')
    : '';
};

export const DatePicker: FC<DatePickerProps> = ({
  formikFieldSelector,
  theme,
  allowFutureDates = true,
}) => {
  const { language } = useEnvironment();
  const { getFieldLabel } = useFieldLabel();
  const { getFieldPrivacyAriaLabel } = useFieldPrivacyAriaLabel();
  const [formikField, , helpers] = useFormikField<Field | null>(
    formikFieldSelector,
  );

  const field = formikField.value;
  const locale = language as DatePickerInputProps['locale'];
  const isFieldThemeBox = theme === TextFieldTheme.Box;

  if (!field) {
    return null;
  }

  const { required, infoTooltipText, value, id } = field;

  const handleDateChange = (newDate: Date) => {
    const year = newDate.getFullYear();
    const month = (newDate.getMonth() + 1).toString().padStart(2, '0');
    const day = newDate.getDate().toString().padStart(2, '0');
    helpers.setValue({
      ...field,
      value: `${year}-${month}-${day}`,
    });
  };

  return (
    <div className={classes.root}>
      <FieldLabel
        htmlFor={id}
        text={getFieldLabel(field)}
        isFieldRequired={required}
        infoTooltipText={infoTooltipText}
      />
      <FieldPrivacyIcon
        formikFieldSelector={formikFieldSelector}
        aria-label={getFieldPrivacyAriaLabel(field)}
      >
        <DatePickerInput
          value={value && new Date(value.replace(/-/g, '/'))}
          className={st(classes.field, { box: isFieldThemeBox })}
          inputTheme={theme}
          showMonthDropdown
          showYearDropdown
          inputWidth="100%"
          locale={locale}
          placeholderText={getDatePattern(language)}
          filterDate={
            allowFutureDates ? undefined : (date) => date < new Date()
          }
          onChange={handleDateChange}
        />
      </FieldPrivacyIcon>
    </div>
  );
};
